/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { ComponentType } from "react";

import { RemoteConfigStateMachine } from "../remote-config";
import { firebaseApp } from "..";

interface WithChildren {
  children?: React.ReactNode | React.ReactNode[];
}

type SafeChild = WithChildren & object;

const withFirebaseSafety = <P extends SafeChild>(Component: ComponentType<P>) => {
  const WithFirebaseComponent = (props: P) => {
    return React.useMemo(() => {
      if (firebaseApp) {
        // If firebaseApp is not null, render the component with its props
        return <Component {...props} />;
      } else {
        // If firebaseApp is null, decide how to render the children
        // Here, we assume you want to render the children of Component if they exist
        if (props.children) {
          // If Component has children, render them
          return <RemoteConfigStateMachine>{props.children}</RemoteConfigStateMachine>;
        } else {
          // If Component does not have children, you might render null or a placeholder
          return null; // or <div>No Firebase available</div> or any fallback UI
        }
      }
    }, [firebaseApp, props]);
  };

  // Assigning a display name to the returned component
  WithFirebaseComponent.displayName = `withFirebaseSafety(${Component.displayName || Component.name})`;

  return WithFirebaseComponent;
};

export default withFirebaseSafety;
